import { Info } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// 공용 ⓘ 툴팁 — React portal 기반 CSS bubble (shadcn/radix 미도입 환경).
//
// Portal 을 사용하는 이유:
//   - 부모 컨테이너에 `overflow: hidden` (예: .pca-table-wrap) 이 있으면 `position:
//     absolute` 로 그린 bubble 이 테이블 영역 안으로 클리핑된다.
//   - `createPortal(bubble, document.body)` + `position: fixed` 조합으로 모든 overflow
//     경계를 탈출한다.
//
// 접근성:
//   · role="note" + aria-label — 스크린리더가 툴팁 내용 읽음.
//   · tabIndex=0 — 키보드 포커스로 툴팁 표시.
//   · 마우스 hover/focus 모두에서 bubble 표시.

interface InfoTooltipProps {
  /** 툴팁 본문 (짧은 한 단락 권장, 최대 400px 래핑). */
  label: string;
  /** 툴팁 버블 위치 — default 'top'. 우측 경계에 겹칠 때 'left' 사용. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** 아이콘 크기. 테이블 헤더는 12, 폼 label 은 14 권장. */
  size?: number;
  /** data-testid — 테스트에서 특정 툴팁을 타겟할 때. */
  testId?: string;
  /** 사용자 정의 클래스 (상속 컨텍스트 색 맞추기 등). */
  className?: string;
}

interface BubblePos {
  top: number;
  left: number;
}

const GAP = 8;

export function InfoTooltip({
  label,
  placement = 'top',
  size = 12,
  testId,
  className,
}: InfoTooltipProps): JSX.Element {
  const iconRef = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<BubblePos>({ top: 0, left: 0 });

  const show = useCallback(() => {
    const el = iconRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    // placement 별 anchor 좌표 (fixed 위치). transform 은 CSS 에서 처리.
    let top = r.top;
    let left = r.left;
    switch (placement) {
      case 'top':
        top = r.top - GAP;
        left = cx;
        break;
      case 'bottom':
        top = r.bottom + GAP;
        left = cx;
        break;
      case 'left':
        top = cy;
        left = r.left - GAP;
        break;
      case 'right':
        top = cy;
        left = r.right + GAP;
        break;
    }
    setPos({ top, left });
    setVisible(true);
  }, [placement]);

  const hide = useCallback(() => setVisible(false), []);

  const cls =
    'info-tooltip info-tooltip--' + placement + (className ? ' ' + className : '');

  return (
    <span
      ref={iconRef}
      className={cls}
      tabIndex={0}
      role="note"
      aria-label={label}
      data-testid={testId}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <Info size={size} strokeWidth={1.75} aria-hidden="true" />
      {visible &&
        createPortal(
          <span
            className={`info-tooltip__bubble info-tooltip__bubble--${placement}`}
            role="tooltip"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
}
